(ns machtfit.stepchall.charts
  (:require [cljs-time.coerce :as time.coerce]
            [cljs-time.core :as time]
            [cljs-time.periodic :as periodic]
            [machtfit.format :as format]
            [machtfit.human :as human]
            [machtfit.stepchall.date :as date]
            [machtfit.translation :refer [tr t trp-with-place-holders]]
            [medley.core :refer [map-keys]]
            [re-frame.core :as rf]))

(rf/reg-event-db ::charts-navigation-clicked
  (fn [db [_ stats-date]]
    (assoc-in db [:ui :stepchall :stats-date] stats-date)))

(defn- charts-navigation-button [{:keys [stats-date title]}]
  [:button.sc_charts-navigation-button {:class (when (= stats-date @(rf/subscribe [::date/stats-date])) "active")
                                        :on-click #(rf/dispatch [::charts-navigation-clicked stats-date])} title])

(defn- charts-navigation []
  (let [today @(rf/subscribe [:get [:server-info :reference-today]])
        team-building-start @(rf/subscribe [:get [:stepchall :team-building-start-date]])
        yesterday (time/minus today (time/days 1))
        ereyesterday (time/minus today (time/days 2))
        date->title #(-> %
                         time/day-of-week
                         human/weekday
                         :long
                         t)]
    [:div.sc_charts-navigation
     [charts-navigation-button {:stats-date :all
                                :title (tr "Gesamt")}]
     (when (>= ereyesterday team-building-start)
       [charts-navigation-button {:stats-date (format/date->keyword ereyesterday)
                                  :title (date->title ereyesterday)}])
     (when (>= yesterday team-building-start)
       [charts-navigation-button {:stats-date (format/date->keyword yesterday)
                                  :title (date->title yesterday)}])
     [charts-navigation-button {:stats-date (format/date->keyword today)
                                :title (tr "Heute")}]]))

(defn- percentage-of [total part]
  (* 100 (/ part total)))

(defn- to-day [day]
  (case day
    1 "Mo"
    2 "Di"
    3 "Mi"
    4 "Do"
    5 "Fr"
    6 "Sa"
    7 "So"))

(rf/reg-sub ::step-counts-daily
  :<- [:get [:ui :page :subpage]]
  :<- [:get [:stepchall :participant :step-counts :daily]]
  :<- [:get [:stepchall :team :step-counts :daily]]

  (fn [[page step-counts-participant step-counts-team] _]
    (->> (case page
           :my-team step-counts-team
           :my-steps step-counts-participant)
         (map-keys #(-> % name time.coerce/to-date-time)))))

(rf/reg-sub ::first-entry-date
  :<- [::step-counts-daily]

  (fn [step-counts-daily _]
    (let [first-entry (->> step-counts-daily
                           (remove #(= 0 (val %)))
                           sort
                           first)]
      (when first-entry (key first-entry)))))

(rf/reg-sub ::dates-since-start
  :<- [:get [:stepchall :challenge-start-date]]
  :<- [::first-entry-date]
  :<- [:get [:server-info :reference-today]]

  (fn [[challenge-start-date first-entry-date today] _]
    (let [week-ago (time/minus today (time/days 6))
          start-date (cond
                       (and (some? first-entry-date)
                            (<= first-entry-date challenge-start-date)
                            (<= first-entry-date week-ago)) first-entry-date
                       (<= challenge-start-date week-ago) challenge-start-date
                       :else week-ago)]
      (periodic/periodic-seq start-date (time/plus today (time/days 1)) (time/days 1)))))

(rf/reg-sub ::days-data
  :<- [::dates-since-start]
  :<- [::step-counts-daily]
  :<- [:get [:stepchall :challenge-start-date]]
  :<- [:get [:stepchall :challenge-end-date]]
  :<- [:get [:stepchall :participant :deactivated-at-date]]
  :<- [:get [:ui :page :subpage]]

  (fn [[dates-since-start step-counts-daily challenge-start-date challenge-end-date deactivated-at-date subpage] _]
    (into {} (for [date dates-since-start]
               [date {:step-count (step-counts-daily date)
                      :weekday (time/day-of-week date)
                      :has-start-label? (= date challenge-start-date)
                      :has-end-label? (= date challenge-end-date)
                      :has-deactivated-label? (and (some? deactivated-at-date)
                                                   (= date deactivated-at-date)
                                                   (= :my-steps subpage))
                      :in-competition? (and (time/within? {:start challenge-start-date
                                                           :end (time/plus challenge-end-date (time/days 1))}
                                                          date)
                                            (or (not= subpage :my-steps)
                                                (nil? deactivated-at-date)
                                                (time/before? date deactivated-at-date)))}]))))

(rf/reg-sub ::data-for-date
  :<- [::days-data]

  (fn [days-data [_ date]]
    (days-data date)))

(rf/reg-sub ::daily-step-target
  :<- [:get [:stepchall :challenge-start-date]]
  :<- [:get [:stepchall :challenge-end-date]]
  :<- [:get [:stepchall :goal]]

  (fn [[start end goal] _]
    (int (Math/ceil (/ goal (+ 1 (time/in-days (time/interval start end))))))))

(rf/reg-sub ::max-step-count
  :<- [::step-counts-daily]

  (fn [step-counts-daily _]
    (apply max (vals step-counts-daily))))

(defn- bar [{:keys [chart-ceiling date]}]
  (let [{:keys [step-count
                weekday
                in-competition?
                has-start-label?
                has-end-label?
                has-deactivated-label?]} @(rf/subscribe [::data-for-date date])
        weekday-name (to-day weekday)]
    [:div.sc_bar-chart {:style {:height (str (percentage-of chart-ceiling step-count) "%")
                                :opacity (when-not in-competition? "0.5")}}
     [:span.sc_bar-chart-weekday {:class (when (= weekday 7) "sc_bar-chart-weekday-bold")} (tr weekday-name)]
     (when has-start-label? [:span.sc_bar-chart-separator.start [:span (tr "Start")]])
     (when has-end-label? [:span.sc_bar-chart-separator.end [:span (tr "Ende")]])
     (when has-deactivated-label? [:span.sc_bar-chart-separator.deactivated [:span (tr "Wertung beendet")]])]))

(defn- bar-chart []
  (let [daily-step-target @(rf/subscribe [::daily-step-target])
        chart-ceiling (max @(rf/subscribe [::max-step-count]) daily-step-target)]
    [:div.sc_bar-charts
     [:div.sc_bar-charts-inner
      [:div.sc_bar-charts-average {:style {:bottom (str (percentage-of chart-ceiling daily-step-target) "%")}}
       [:span (tr "Tagesziel")]]
      (doall
       (for [date @(rf/subscribe [::dates-since-start])]
         ^{:key date} [bar {:date date
                            :chart-ceiling chart-ceiling}]))]]))

(defn- donut-chart-circle [{:keys [color-from color-to percentage round]}]
  (let [percentage-capped (min percentage (* 100 (inc round)))
        ratio-capped (/ percentage-capped 100)
        ratio-this-round (- ratio-capped round)
        percentage-this-round (* 100 ratio-this-round)]
    [:g
     [:defs
      [:linearGradient {:id (str "gradient" round) :y2 "100%", :x2 "0%", :y1 "0%", :x1 "0%"}
       [:stop {:stop-color color-from, :offset "0%"}]
       [:stop {:stop-color color-to, :offset "100%"}]]]
     [:circle.donut-segment
      {:style {:animation-duration (str ratio-this-round "s")
               :animation-delay (str round "s")}
       :stroke-linecap "round"
       :stroke-dashoffset "25"
       :stroke-dasharray (str percentage-this-round " " (- 100 percentage-this-round))
       :stroke-width "4"
       :stroke (str "url(#gradient" round ")")
       :fill "transparent"
       :r "15.91549430918954"
       :cy "21"
       :cx "21"}]]))

(defn- donut-chart []
  (let [stats-date @(rf/subscribe [::date/stats-date])
        daily-step-target @(rf/subscribe [::daily-step-target])
        {:keys [step-count in-competition?]} @(rf/subscribe [::data-for-date (-> stats-date name time.coerce/to-date-time)])
        percentage (percentage-of daily-step-target step-count)
        main-unit-plural @(rf/subscribe [:get [:stepchall :main-unit :plural]])
        content [:<>
                 [:span (format/decimal step-count)]
                 [:span.unit (t main-unit-plural)]
                 [:hr]
                 [:span.daily-target (format/decimal daily-step-target)]
                 [:span.unit (tr "Tagesziel")]]
        medal-title (cond
                      (>= percentage 200) (trp-with-place-holders
                                           {:de "{number}x Tagesziel erreicht"
                                            :en "{number}x daily goal reached"}
                                           {"number" (quot percentage 100)})
                      (>= percentage 100) (t {:de "Tagesziel erreicht"
                                              :en "Daily goal reached"}))]
    ^{:key (str stats-date "-" percentage)}
    [:div.sc_donut {:style {:opacity (when-not in-competition? "0.5")}}
     (when medal-title
       [:div.reached-daily-target {:style {:animation--delay (str (/ percentage 100) "s")}}
        [:span.emoji "\uD83C\uDFC5"]
        [:span.factor medal-title]])
     [:div.sc_donut-square
      [:span.sc_donut-label {:style {:opacity "0"
                                     :position "relative"
                                     :height "0"}} content]
      [:span.sc_donut-label content]
      [:svg.donut
       {:viewBox "0 0 42 42", :height "100%", :width "100%" :preserveAspectRatio "none"}

       [:circle.donut-hole
        {:fill "#fff", :r "15.91549430918954", :cy "21", :cx "21"}]
       [:circle.donut-ring
        {:stroke-width "4"
         :stroke "#eee"
         :fill "transparent"
         :r "15.91549430918954"
         :cy "21"
         :cx "21"}]
       (when (> percentage 0)
         [donut-chart-circle {:color-from "#34b78f"
                              :color-to "#45f5c0"
                              :percentage percentage
                              :round 0}])
       (when (> percentage 100)
         [donut-chart-circle {:color-from "#008ca0"
                              :color-to "#86e3f0"
                              :percentage percentage
                              :round 1}])
       (when (> percentage 200)
         [donut-chart-circle {:color-from "#e62e73"
                              :color-to "#fce08e"
                              :percentage percentage
                              :round 2}])
       [:circle.donut-dot
        {:r "15.91549430918954"
         :stroke-dashoffset "25"
         :stroke-dasharray "0 100"
         :fill "transparent"
         :stroke "#fff"
         :cy "21"
         :stroke-linecap "round"
         :cx "21"
         :stroke-width "2"
         :style {:transform (str "rotate(" (/ (* 360 percentage) 100) "deg)")
                 :transform-origin "center"
                 :animation-duration (str (/ percentage 100) "s")}}]]]]))

(defn charts-container []
  [:<>
   [charts-navigation]
   [:div.sc_my-steps-stats
    (if (= @(rf/subscribe [::date/stats-date]) :all)
      [bar-chart]
      [donut-chart])]])
